/**
 * What an attorney can do with an intake that's out with the client, shared
 * by the tracking pane's toolbar and the explorer's context menu: copy the
 * link, remind (optionally with a message), extend, revoke, cancel,
 * duplicate as a new draft (which opens it), save as a firm template, and
 * delete a draft.
 */
import { useCallback } from "react";
import { toast } from "sonner";

import { OutgoingIntakesAPI } from "@/api/outgoingIntakesApi";
import type { OutgoingIntake } from "@/api/types/outgoingIntake";

import { useRequestsIde } from "./context/RequestsIdeContext";
import { useRequestsData } from "./data/RequestsDataContext";
import { copyIntakeLink, toastEmailFailed } from "./linkToasts";
import { intakeArtifact } from "./model";

const message = (e: unknown, fallback: string) =>
  e instanceof Error ? e.message : fallback;

export const useIntakeActions = () => {
  const { clientId, invalidate, open } = useRequestsIde();
  const data = useRequestsData();

  const applied = useCallback(
    (intake: OutgoingIntake) => {
      data.applyIntake(intake);
      invalidate(["intakes", "activity"]);
    },
    [data, invalidate]
  );

  const copyLink = useCallback(
    (intake: OutgoingIntake) =>
      copyIntakeLink(clientId, intake.outgoing_intake_id),
    [clientId]
  );

  const remind = useCallback(
    async (intake: OutgoingIntake, note?: string): Promise<boolean> => {
      try {
        const res = await OutgoingIntakesAPI.remind(
          clientId,
          intake.outgoing_intake_id,
          {
            message: note?.trim() || undefined
          }
        );
        applied(res.outgoing_intake);
        if (res.email_delivered === false)
          toastEmailFailed(clientId, intake.outgoing_intake_id);
        else
          toast.success(
            res.reissued_link
              ? "Reminder sent with a fresh link"
              : "Reminder sent"
          );
        return true;
      } catch (e) {
        toast.error(message(e, "Couldn't send the reminder"));
        return false;
      }
    },
    [applied, clientId]
  );

  const extend = useCallback(
    async (intake: OutgoingIntake, days: number) => {
      try {
        const updated = await OutgoingIntakesAPI.extend(
          clientId,
          intake.outgoing_intake_id,
          days
        );
        applied(updated);
        toast.success(`Link extended by ${days} days`);
      } catch (e) {
        toast.error(message(e, "Couldn't extend the link"));
      }
    },
    [applied, clientId]
  );

  const revoke = useCallback(
    async (intake: OutgoingIntake) => {
      try {
        applied(
          await OutgoingIntakesAPI.revoke(clientId, intake.outgoing_intake_id)
        );
        toast.success("Link revoked", {
          description:
            "The client sees that the link no longer works. Send again to issue a new one."
        });
      } catch (e) {
        toast.error(message(e, "Couldn't revoke the link"));
      }
    },
    [applied, clientId]
  );

  const cancel = useCallback(
    async (intake: OutgoingIntake) => {
      try {
        applied(
          await OutgoingIntakesAPI.cancel(clientId, intake.outgoing_intake_id)
        );
        toast.success("Intake cancelled");
      } catch (e) {
        toast.error(message(e, "Couldn't cancel the intake"));
      }
    },
    [applied, clientId]
  );

  const duplicate = useCallback(
    async (intake: OutgoingIntake, schema = intake.schema) => {
      try {
        const title = `${intake.title} (copy)`;
        const created = await OutgoingIntakesAPI.createForForeignNational(
          clientId,
          {
            template_ids: ["custom"],
            title
          }
        );
        const patched = await OutgoingIntakesAPI.patch(
          clientId,
          created.outgoing_intake_id,
          {
            form_schema: schema as unknown[],
            title,
            ...(intake.recipient_email
              ? { recipient_email: intake.recipient_email }
              : {}),
            ...(intake.attorney_note
              ? { attorney_note: intake.attorney_note }
              : {})
          }
        );
        applied(patched);
        open(intakeArtifact(patched));
        toast.success("Duplicated as a draft", {
          description: `Opened “${title}”.`
        });
      } catch (e) {
        toast.error(message(e, "Couldn't duplicate the intake"));
      }
    },
    [applied, clientId, open]
  );

  const saveAsTemplate = useCallback(
    async (intake: OutgoingIntake) => {
      try {
        const { template, already_saved: alreadySaved } =
          await OutgoingIntakesAPI.saveIntakeAsTemplate(
            clientId,
            intake.outgoing_intake_id,
            { name: intake.title || "Intake" }
          );
        data.applySavedTemplate(template);
        toast.success(
          alreadySaved
            ? `“${template.name}” is already a firm template`
            : `Saved “${template.name}” as a firm template`,
          { description: "Find it in Firm Knowledge › Intake Templates." }
        );
      } catch (e) {
        toast.error(message(e, "Couldn't save the template"));
      }
    },
    [clientId, data]
  );

  /** Delete a draft for good. */
  const remove = useCallback(
    async (intake: OutgoingIntake): Promise<boolean> => {
      try {
        await OutgoingIntakesAPI.delete(clientId, intake.outgoing_intake_id);
        invalidate(["intakes", "activity"]);
        await data.refetch(["intakes"]);
        return true;
      } catch (e) {
        toast.error(message(e, "Couldn't delete the request"));
        return false;
      }
    },
    [clientId, data, invalidate]
  );

  return {
    remove,
    copyLink,
    remind,
    extend,
    revoke,
    cancel,
    duplicate,
    saveAsTemplate
  };
};
